import { useCallback, useMemo, useState } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useFilterDraft } from '@/hooks/useFilterDraft';
import { countActiveFilters } from '@/utils/filters';
import { TEACHER_LIST_PAGE_SIZE } from '../constants/teacherSubmission.constants';
import type { TeacherListParams } from '../types/teacherSubmission.types';
import {
  clearDependentFilters,
  toCountsBySubmitterParams,
  toFilterValues,
  toSearchFilters,
  toSummaryParams,
  type TeacherSubmissionFilters,
} from '../utils/teacherSubmissionFilters';

const teacherSubmissionsRouteApi = getRouteApi('/_auth/settings/teacher-submissions/');

export function useTeacherSubmissionFilters() {
  const search = teacherSubmissionsRouteApi.useSearch();
  const navigate = teacherSubmissionsRouteApi.useNavigate();
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const appliedValues = useMemo(() => toFilterValues(search), [search]);
  const summaryParams = useMemo(() => toSummaryParams(search), [search]);
  const listParams = useMemo<TeacherListParams>(
    () => ({ ...summaryParams, status: search.teacherStatus, pageSize: TEACHER_LIST_PAGE_SIZE }),
    [summaryParams, search.teacherStatus],
  );
  const countsBySubmitterParams = useMemo(() => toCountsBySubmitterParams(search), [search]);

  const { draftValues, setDraftValues, applyValues, hasPendingChanges } = useFilterDraft({
    appliedValues,
    shouldWaitForApply: true,
    onApply: (filters) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toSearchFilters(filters) }),
        replace: true,
        resetScroll: false,
      });
    },
  });

  const appliedSearch = search.search;

  const handleSearchApply = useCallback(
    (searchText: string) => {
      const nextSearch = searchText.trim() || undefined;
      if (nextSearch === appliedSearch) return;
      void navigate({
        search: (previous) => ({ ...previous, search: nextSearch }),
        replace: true,
        resetScroll: false,
      });
    },
    [navigate, appliedSearch],
  );

  function handleFilterChange(nextFilters: TeacherSubmissionFilters) {
    setDraftValues((previous) => clearDependentFilters(previous, nextFilters));
  }

  function applyFilterValues(nextFilters: TeacherSubmissionFilters) {
    applyValues(clearDependentFilters(draftValues, nextFilters));
  }

  return {
    searchText: search.search ?? '',
    filterValues: draftValues,
    isFilterOpen,
    setIsFilterOpen,
    activeFilterCount: countActiveFilters(appliedValues),
    hasPendingChanges,
    listParams,
    summaryParams,
    countsBySubmitterParams,
    handleSearchApply,
    handleFilterChange,
    applyFilterValues,
  };
}
